import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import CopiedToast from "./CopiedToast";
import { NEW_MAP_DATA } from "../store/MapConstants.js";

const BUILDING_NAMES: string[] = Array.from(
  new Set((NEW_MAP_DATA as { name: string }[]).map((b) => b.name)),
).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));

const DirectionsModal = styled.div`
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(+75%);
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  z-index: 5100;
  min-width: 280px;
`;

const DirectionsHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  position: relative;
`;

const DirectionsTitle = styled.div`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: black;
`;

const DirectionsCloseButton = styled.button`
  position: absolute;
  right: -10px;
  top: -10px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 16px;
  font-weight: bold;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const DirectionsInputRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  position: relative;
`;

const DirectionsIcon = styled.span`
  margin-right: 0.75rem;
  font-size: 1.5rem;
  width: 30px;
`;

const DirectionsInputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const DirectionsInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  outline: none;

  &:focus {
    border-color: #1a73e8;
  }

  &::placeholder {
    color: #999;
  }
`;

const SuggestionsDropdown = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  max-height: 160px;
  overflow-y: auto;
  z-index: 5200;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
`;

const SuggestionItem = styled.li`
  padding: 0.4rem 1rem;
  font-size: 0.9rem;
  cursor: pointer;
  color: #333;

  &:hover {
    background: #f0f4ff;
    color: #1a73e8;
  }
`;

const DirectionsButtonsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  gap: 0.5rem;
`;


type Props = {
  onClose: () => void;
  from: string;
  setFrom: (v: string) => void;
  to: string;
  setTo: (v: string) => void;
  onSearch: () => Promise<void> | void;
  isLoading: boolean;
  onCancel?: () => void;
  routeData?: any;
};

function filterBuildings(query: string): string[] {
  if (!query.trim()) return [];
  const q = query.toLowerCase();
  return BUILDING_NAMES.filter((name) => name.toLowerCase().includes(q));
}

export default function Directions({
  onClose,
  from,
  setFrom,
  to,
  setTo,
  onSearch,
  isLoading,
  onCancel,
  routeData,
}: Props) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const [isError, setIsError] = useState(false);
  const [fromSuggestions, setFromSuggestions] = useState<string[]>([]);
  const [toSuggestions, setToSuggestions] = useState<string[]>([]);
  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => setIsCopied(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  useEffect(() => {
    if (isError) {
      const timer = setTimeout(() => setIsError(false), 2500);
      return () => clearTimeout(timer);
    }
  }, [isError]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (fromRef.current && !fromRef.current.contains(e.target as Node)) {
        setFromSuggestions([]);
      }
      if (toRef.current && !toRef.current.contains(e.target as Node)) {
        setToSuggestions([]);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleFromChange(value: string) {
    setFrom(value);
    setFromSuggestions(filterBuildings(value));
  }

  function handleToChange(value: string) {
    setTo(value);
    setToSuggestions(filterBuildings(value));
  }

  function selectFrom(name: string) {
    setFrom(name);
    setFromSuggestions([]);
  }

  function selectTo(name: string) {
    setTo(name);
    setToSuggestions([]);
  }

  function handleShare() {
    const url = `${window.location.origin}/map?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    navigator.clipboard.writeText(url).then(() => {
      setIsCopied(true);
    });
  }

  return (
    <>
      <DirectionsModal>
        <DirectionsHeader>
          <DirectionsTitle>{t("Smer") || "Direction"}</DirectionsTitle>
          <DirectionsCloseButton onClick={onClose}>✕</DirectionsCloseButton>
        </DirectionsHeader>

        <DirectionsInputRow ref={fromRef}>
          <DirectionsIcon>🏳️</DirectionsIcon>
          <DirectionsInputWrapper>
            <DirectionsInput
              type="text"
              placeholder={t("Odkiaľ") || "From"}
              value={from}
              onChange={(e) => handleFromChange(e.target.value)}
              onFocus={() => setFromSuggestions(filterBuildings(from))}
              autoComplete="off"
            />
            {fromSuggestions.length > 0 && (
              <SuggestionsDropdown>
                {fromSuggestions.map((name) => (
                  <SuggestionItem key={name} onMouseDown={() => selectFrom(name)}>
                    {name}
                  </SuggestionItem>
                ))}
              </SuggestionsDropdown>
            )}
          </DirectionsInputWrapper>
        </DirectionsInputRow>

        <DirectionsInputRow ref={toRef}>
          <DirectionsIcon>🏁</DirectionsIcon>
          <DirectionsInputWrapper>
            <DirectionsInput
              type="text"
              placeholder={t("Kam") || "To"}
              value={to}
              onChange={(e) => handleToChange(e.target.value)}
              onFocus={() => setToSuggestions(filterBuildings(to))}
              autoComplete="off"
            />
            {toSuggestions.length > 0 && (
              <SuggestionsDropdown>
                {toSuggestions.map((name) => (
                  <SuggestionItem key={name} onMouseDown={() => selectTo(name)}>
                    {name}
                  </SuggestionItem>
                ))}
              </SuggestionsDropdown>
            )}
          </DirectionsInputWrapper>
        </DirectionsInputRow>

        <DirectionsButtonsRow>
          <button
            type="button"
            className="map-marker__popup-btn"
            onClick={() => {
              const fromValid = BUILDING_NAMES.includes(from.trim());
              const toValid = BUILDING_NAMES.includes(to.trim());
              if (!fromValid || !toValid) {
                setIsError(true);
                return;
              }
              onSearch();
            }}
            disabled={isLoading}
          >
            {isLoading ? "..." : (t("OK") || "OK")}
          </button>
          <button
            type="button"
            className="map-marker__popup-btn"
            onClick={handleShare}
            title={t("Zdieľať") || "Share"}
            disabled={!routeData}
          >
            Share
          </button>
          <button
            type="button"
            className="map-marker__popup-btn"
            onClick={() => {
              if (onCancel) onCancel();
              else {
                setFrom("");
                setTo("");
                onClose();
              }
            }}
            title={t("Zrušiť") || "Cancel"}
          >
            Cancel
          </button>
        </DirectionsButtonsRow>
      </DirectionsModal>

      <CopiedToast visible={isCopied} />
      <CopiedToast visible={isError} message={t("buildingNotFound") as string} />
    </>
  );
}

Directions.defaultProps = {
  onCancel: undefined,
  routeData: undefined,
};
